import {
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	Chip,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	Radio,
	RadioGroup,
	Stack,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SeriesReportDownloadButton from '@/pages/report-manage/report-list/components/SeriesReportDownloadButton';
import { FormType } from '@/api/enum/formType.enum';
import { useCallback, useEffect, useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import useModal from '@/common/components/ConfirmationDialog/useModal';

import {
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import RichTextDisplay from '@/common/components/form-view/RichTextDisplay';
import Day21InfoForm from './components/Day21InfoForm';
import AssignmentReturnedOutlinedIcon from '@mui/icons-material/AssignmentReturnedOutlined';
import { ExerciseColor, OnoffType } from '@/api/enum/common.enum';
import { showLabel } from '@/utils/helper';
import AppImageView from '@/common/components/form-view/AppImageView';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { MeditationSinglePageFilter } from '@/api/DTO/exercise/meditation.interface';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import qs from 'qs';
import { GridColDef } from '@mui/x-data-grid';
import {
	useDeleteDay21Mutation,
	useGetDay21DetailQuery,
	useGetDay21SingleListQuery,
	useSetDay21StatusMutation,
} from '@/api/exercise/day21ApiSlice';
import { Series21DayPageResDTO } from '@/api/DTO/exercise/day21.interface';
import QuestionnaireForm from './components/QuestionnaireForm';
import Day21QuestionnaireDetailPage from './Day21QuestionnaireDetailPage';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function Day21DetailPage() {
	const { t } = useTranslation();
	const [type, setType] = useState<FormType>(FormType.view);
	const [questionnaireType, setQuestionnaireType] = useState<FormType>(
		FormType.create
	);
	const showModal = useModal();
	const [deleteDay21] = useDeleteDay21Mutation();
	const { day21Id } = useParams();
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('zh');
	const [currentTopTab, setCurrentTopTab] = useState('base');
	const [setDay21Status] = useSetDay21StatusMutation();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const [searchParams, setSearchParams] = useSearchParams();

	const statusList = [
		{ id: OnoffType.off, name: t('day21.noCompleted') },
		{ id: OnoffType.on, name: t('day21.completed') },
	];

	const seriesStatusList = [
		{ id: OnoffType.off, name: t('day21.off') },
		{ id: OnoffType.on, name: t('day21.on') },
	];

	const colorList = [
		{ id: ExerciseColor.AQUA, name: t('day21.AQUA') },
		{ id: ExerciseColor.ORANGE, name: t('day21.ORANGE') },
		{ id: ExerciseColor.CORAL, name: t('day21.CORAL') },
		{ id: ExerciseColor.PINK, name: t('day21.PINK') },
		{ id: ExerciseColor.PURPLE, name: t('day21.PURPLE') },
		{ id: ExerciseColor.LAVENDER, name: t('day21.LAVENDER') },
		{ id: ExerciseColor.BLUE, name: t('day21.BLUE') },
		{ id: ExerciseColor.CX, name: t('meditation.CX') },
		{ id: ExerciseColor.MTR, name: t('meditation.MTR') },
	];

	const exerciseTypeList = [
		{ id: 1, name: t('day21.audio') },
		{ id: 2, name: t('day21.video') },
	];
	const canList = [
		{ id: true, name: t('day21.can') },
		{ id: false, name: t('day21.cannot') },
	];
	const yesOrNoList = [
		{ id: true, name: t('day21.yes') },
		{ id: false, name: t('day21.no') },
	];

	const { data: details } = useGetDay21DetailQuery(day21Id || '', {
		refetchOnMountOrArgChange: true,
	});
	useEffect(() => {
		if (!details?.data.hasQuestion) {
			setCurrentTopTab('base');
		}
	}, [details]);
	const defaultPropertyFilter: MeditationSinglePageFilter = {
		pagination: { ...initialPagination, size: 50 },
		sortings: [],
		id: day21Id || '',
	};

	const defaultValues: DefaultValues<MeditationSinglePageFilter> =
		defaultPropertyFilter;
	const methods = useForm<MeditationSinglePageFilter>({
		defaultValues,
	});

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as MeditationSinglePageFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const transformedSearchParams: MeditationSinglePageFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetDay21SingleListQuery(
			{ ...transformedSearchParams, id: day21Id },
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback(
		(response?: Series21DayPageResDTO[]) => {
			if (response == null) return;
			const newResponse: DataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					createdAt: !item.createdAt
						? '-'
						: dayjs(item.createdAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	); // watch fields change

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};
	const handleTopTabChange = (
		event: React.SyntheticEvent,
		newValue: string
	) => {
		setCurrentTopTab(newValue);
	};

	const handleDelete = async () => {
		if (details?.data.status == OnoffType.on) {
			return enqueueSnackbar(t('snackbar.noDeleteMeditation'), {
				variant: 'error',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('day21.deleteSeries')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('day21.isDeleteSeries')}</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteDay21(day21Id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteMeditationSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						navigate('/exercise-manage/day21-list', { replace: true });
					}
				}}
			/>
		));
	};

	const handleSetStatus = async (type: number) => {
		const response = await setDay21Status({
			seriesPublishDTOList: [
				{ seriesId: day21Id || '', published: type == 1 ? true : false },
			],
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateExerciseStatusSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
	};

	const columns: GridColDef[] = [
		{
			field: 'day',
			headerName: t('day21.exerciseDay'),
			flex: 25,
		},
		{
			field: 'titleZh',
			headerName: t('day21.exerciseTitleZh'),
			flex: 25,
		},
		{
			field: 'mediaType',
			headerName: t('day21.exerciseType'),
			flex: 25,
			renderCell: (params) => (
				<>{showLabel(params.row.mediaType, exerciseTypeList)}</>
			),
		},
		{
			field: 'canCountTime',
			headerName: t('day21.canCountTime'),
			flex: 25,
			renderCell: (params) => (
				<>{showLabel(params.row.canCountTime, canList)}</>
			),
		},
		{
			field: 'canCollectPoints',
			headerName: t('day21.canCollectPoints'),
			flex: 25,
			renderCell: (params) => (
				<>{showLabel(params.row.canCollectPoints, canList)}</>
			),
		},
		{
			field: 'rewardPoints',
			headerName: t('achievementList.rewardPoints'),
			flex: 15,
			renderCell: (params: any) => params.row.rewardPoints || '-',
		},
		{
			field: 'rewardDesc',
			headerName: t('achievementList.rewardProp'),
			flex: 25,
			renderCell: (params: any) => params.row.rewardDesc || '-',
		},
		{
			field: 'hasAdditional',
			headerName: t('day21.hasAdditional'),
			flex: 25,
			renderCell: (params) => (
				<>{showLabel(params.row.hasAdditional, yesOrNoList)}</>
			),
		},
		{
			field: 'status',
			headerName: t('day21.exerciseStatus'),
			flex: 25,
			renderCell: (params: any) => (
				<Chip
					color={params.row.status == OnoffType.on ? 'chipPrimary' : 'default'}
					label={showLabel(params.row.status, statusList)}
				/>
			),
		},
		{
			field: 'updatedAt',
			headerName: t('day21.lastUpdateTime'),
			flex: 20,
		},
		{
			field: 'createdAt',
			headerName: t('day21.createTime'),
			flex: 20,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/exercise-manage/day21-list/info/${day21Id}/detail/${params.row.dayId}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.day21Detail')}
					</Typography>
					<Stack direction={'row'} alignItems={'center'}>
						<SeriesReportDownloadButton
							seriesId={day21Id}
							seriesTitle={details?.data.titleZh}
						/>
						<QuestionnaireForm
							type={questionnaireType}
							setType={setQuestionnaireType}
							hasQuestion={details?.data.hasQuestion}
							setCurrentTopTab={setCurrentTopTab}
						/>
						{details?.data.status == OnoffType.on && (
							<Button
								endIcon={<AssignmentReturnedOutlinedIcon />}
								onClick={() => handleSetStatus(0)}
								sx={{ marginRight: '24px' }}
								variant='contained'
							>
								{t('day21.handleOff')}
							</Button>
						)}
						{details?.data.status == OnoffType.off && (
							<Button
								endIcon={<AssignmentReturnedOutlinedIcon />}
								onClick={() => handleSetStatus(1)}
								sx={{ marginRight: '24px' }}
								variant='contained'
							>
								{t('day21.handleOn')}
							</Button>
						)}
						<Button
							endIcon={<DeleteOutlined />}
							onClick={() => handleDelete()}
							sx={{ marginRight: '24px' }}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Stack>
				</Box>

				{details?.data.hasQuestion && (
					<Tabs
						value={currentTopTab}
						onChange={handleTopTabChange}
						sx={{ marginTop: '24px' }}
					>
						<Tab
							label={
								<Typography variant='body1'>
									{t('day21.basicPractice')}
								</Typography>
							}
							value='base'
						/>
						<Tab
							label={
								<Typography variant='body1'>
									{t('day21.questionnaireContent')}
								</Typography>
							}
							value='content'
						/>
					</Tabs>
				)}

				{currentTopTab == 'base' && (
					<>
						<Card
							sx={{
								padding: type == FormType.view ? '0 24px' : 0,
								marginTop: 3,
							}}
						>
							<Box sx={{ padding: type == FormType.view ? 0 : '0 24px' }}>
								<Box
									display='flex'
									justifyContent='space-between'
									sx={{ paddingTop: '24px' }}
								>
									<Typography variant='h6'>{t('day21.record')}</Typography>
								</Box>
								<Grid container spacing={3} marginTop={0} marginBottom={3}>
									<Grid item xs={6}>
										<AppFieldView
											label={t('day21.seriesStatus')}
											valueElement={
												<Chip
													color={
														details?.data.status == OnoffType.on
															? 'chipPrimary'
															: 'default'
													}
													label={showLabel(
														details?.data.status,
														seriesStatusList
													)}
												/>
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<AppFieldView
											label={t('day21.createTime')}
											value={
												!details?.data.createdAt
													? '-'
													: dayjs(details?.data.createdAt)
															.locale('en')
															.format(DISPLAY_DATETIME_12H_FORMAT)
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<AppFieldView
											label={t('day21.lastUpdateTime')}
											value={
												!details?.data.updatedAt
													? '-'
													: dayjs(details?.data.updatedAt)
															.locale('en')
															.format(DISPLAY_DATETIME_12H_FORMAT)
											}
										/>
									</Grid>
								</Grid>
							</Box>

							<Divider />
							{(() => {
								switch (type) {
									case FormType.view:
										return (
											<>
												<Box
													display='flex'
													justifyContent='space-between'
													sx={{ paddingTop: '24px' }}
												>
													<Typography variant='h6'>
														{t('day21.text')}
													</Typography>
													<Button
														endIcon={<CreateOutlinedIcon />}
														color='secondary'
														onClick={() => {
															setType(FormType.edit);
														}}
													>
														{t('button.edit')}
													</Button>
												</Box>
												<Grid
													container
													spacing={3}
													marginTop={0}
													marginBottom={3}
												>
													<Grid item xs={12}>
														<Tabs value={currentTab} onChange={handleTabChange}>
															<Tab
																label={
																	<Typography variant='body1'>
																		{t('day21.zh')}
																	</Typography>
																}
																value='zh'
															/>
															<Tab
																label={
																	<Typography variant='body1'>
																		{t('day21.en')}
																	</Typography>
																}
																value='en'
															/>
														</Tabs>
													</Grid>
													<Grid item xs={6}>
														<AppFieldView
															label={t('day21.title')}
															value={
																currentTab == 'zh'
																	? details?.data.titleZh
																	: currentTab == 'en'
																	? details?.data.titleEn
																	: '-'
															}
														/>
													</Grid>
													<Grid item xs={6}>
														<AppFieldView
															label={t('day21.color')}
															value={
																showLabel(details?.data.color, colorList) || '-'
															}
														/>
													</Grid>
													<Grid item xs={6}>
														<AppImageView
															label={t('day21.coverImageAcross')}
															imageElement={
																<>
																	{details?.data.coverImage && (
																		<img
																			src={details?.data.coverImage.url}
																			width={500}
																			height={192}
																			style={{
																				objectFit: 'contain',
																			}}
																		/>
																	)}
																</>
															}
														/>
													</Grid>
													<Grid item xs={6}>
														<AppImageView
															label={t('day21.coverImageHalf')}
															imageElement={
																<>
																	{details?.data.coverImageHalf && (
																		<img
																			src={details?.data.coverImageHalf.url}
																			width={110}
																			height={110}
																			style={{
																				objectFit: 'contain',
																				marginRight: '8px',
																			}}
																		/>
																	)}
																</>
															}
														/>
													</Grid>
												</Grid>
												<Divider />
												<Grid
													container
													spacing={3}
													marginTop={0}
													marginBottom={3}
												>
													<Grid item xs={12}>
														{details?.data && (
															<FormControlLabel
																control={
																	<Checkbox
																		disabled
																		checked={details?.data.hasContent}
																	/>
																}
																label={t('day21.hasContent')}
															/>
														)}
													</Grid>
													{details?.data.hasContent && (
														<>
															{currentTab == 'zh' && (
																<Grid item xs={12}>
																	<RichTextDisplay
																		label={t('day21.content')}
																		editorStateString={
																			details?.data.contentZh || '-'
																		}
																	/>
																</Grid>
															)}
															{currentTab == 'en' && (
																<Grid item xs={12}>
																	<RichTextDisplay
																		label={t('day21.content')}
																		editorStateString={
																			details?.data.contentEn || '-'
																		}
																	/>
																</Grid>
															)}
															<Grid item xs={6}>
																{details?.data && (
																	<FormControl>
																		<RadioGroup
																			row
																			name='mediaType'
																			value={details?.data.mediaType}
																		>
																			<FormControlLabel
																				disabled
																				value={3}
																				control={<Radio />}
																				label={t('day21.image')}
																			/>
																			<FormControlLabel
																				disabled
																				value={2}
																				control={<Radio />}
																				label={t('day21.video')}
																			/>
																		</RadioGroup>
																	</FormControl>
																)}
															</Grid>
															{details?.data.mediaType == 3 && (
																<Grid item xs={6}>
																	<AppImageView
																		label={t('day21.mediaImage')}
																		imageElement={
																			details?.data.media && (
																				<img
																					src={details?.data.media.url}
																					width={110}
																					style={{
																						objectFit: 'contain',
																					}}
																				/>
																			)
																		}
																	/>
																</Grid>
															)}
															{details?.data.mediaType == 2 && (
																<>
																	<Grid item xs={6}>
																		<Grid item xs={12}>
																			<AppFieldView
																				label={t('day21.mediaVideo')}
																				value={details?.data.media?.name || '-'}
																				link={details?.data.media?.url}
																			/>
																		</Grid>
																	</Grid>
																	<Grid item xs={6}>
																		<AppImageView
																			label={t('day21.previewImage')}
																			imageElement={
																				details?.data
																					.introductionMediaVideoCover && (
																					<img
																						src={
																							details?.data
																								.introductionMediaVideoCover
																								?.url
																						}
																						width={390}
																						height={138}
																						style={{
																							objectFit: 'contain',
																						}}
																					/>
																				)
																			}
																		/>
																	</Grid>
																</>
															)}
														</>
													)}
												</Grid>
											</>
										);
									case FormType.edit:
										return (
											<>
												<Box
													display='flex'
													justifyContent='space-between'
													sx={{ padding: '24px', paddingBottom: '0' }}
												>
													<Typography variant='h6'>
														{t('day21.text')}
													</Typography>
												</Box>
												<Day21InfoForm
													type={type}
													setType={setType}
													details={details?.data}
												/>
											</>
										);
								}
							})()}
						</Card>

						<Card sx={{ marginTop: 3 }}>
							<CardContent>
								<Box display='flex' justifyContent='space-between'>
									<Typography variant='h6'>
										{t('day21.exerciseList')}
									</Typography>
								</Box>
								<FormProvider {...methods}>
									<Box marginTop={2}>
										<AppDataGrid
											loading={!isGetPropertySummarySuccess}
											rows={
												transformResponse(propertySummary?.data?.items) ?? []
											}
											rowCount={
												propertySummary?.data?.pagination.totalItems ?? 0
											}
											columns={columns}
											getRowId={(row) => row.dayId}
										/>
									</Box>
								</FormProvider>
							</CardContent>
						</Card>
					</>
				)}

				{currentTopTab == 'content' && <Day21QuestionnaireDetailPage />}
			</Box>
		</>
	);
}
